import React, { useState, useEffect } from 'react';
import { API } from '../../services/api';
import MediaUploader from './MediaUploader';
import MediaGrid from './MediaGrid';
import './MediaLibraryModal.css';

function MediaLibraryModal({ isOpen, onClose, onSelectImage }) {
    const [media, setMedia] = useState([]);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [activeTab, setActiveTab] = useState('grid'); // 'grid' or 'upload'

    useEffect(() => {
        if (isOpen) {
            loadMedia();
        }
    }, [isOpen, categoryFilter]);

    const loadMedia = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await API.getMediaLibrary(categoryFilter);
            const mediaData = response.data?.media || response.media || [];
            setMedia(Array.isArray(mediaData) ? mediaData : []);
        } catch (err) {
            console.error('Error loading media:', err);
            setError('Erreur lors du chargement des médias');
        } finally {
            setLoading(false);
        }
    };

    const handleUploadComplete = (uploadedMedia) => {
        // Ajouter le nouveau média à la liste
        setMedia(prev => [uploadedMedia, ...prev]);
        setActiveTab('grid'); // Retourner à la grille
        setSelectedMedia(uploadedMedia); // Sélectionner automatiquement
    };

    const handleDeleteMedia = async (mediaId) => {
        try {
            await API.deleteMedia(mediaId);
            setMedia(prev => prev.filter(m => m.id !== mediaId));
            if (selectedMedia?.id === mediaId) {
                setSelectedMedia(null);
            }
        } catch (err) {
            console.error('Error deleting media:', err);
            // Display the detailed error message from the backend
            setError(err.message || 'Erreur lors de la suppression');
        }
    };

    const handleUpdateMedia = async (mediaId, updates) => {
        try {
            const response = await API.updateMediaMetadata(mediaId, updates);
            setMedia(prev => prev.map(m =>
                m.id === mediaId ? { ...m, ...updates } : m
            ));
            if (selectedMedia?.id === mediaId) {
                setSelectedMedia({ ...selectedMedia, ...updates });
            }
        } catch (err) {
            console.error('Error updating media:', err);
            setError('Erreur lors de la mise à jour');
        }
    };

    const handleSelectAndInsert = () => {
        if (selectedMedia && onSelectImage) {
            onSelectImage(selectedMedia);
        }
    };

    const handleError = (errorMessage) => {
        setError(errorMessage);
        setTimeout(() => setError(''), 5000);
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content media-library-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <h2>📁 Bibliothèque de médias</h2>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>

                {/* Tabs */}
                <div className="media-tabs">
                    <button
                        className={`media-tab ${activeTab === 'grid' ? 'active' : ''}`}
                        onClick={() => setActiveTab('grid')}
                    >
                        Bibliothèque {media.length > 0 && `(${media.length})`}
                    </button>
                    <button
                        className={`media-tab ${activeTab === 'upload' ? 'active' : ''}`}
                        onClick={() => setActiveTab('upload')}
                    >
                        Upload
                    </button>
                </div>

                {/* Category filter (only in grid tab) */}
                {activeTab === 'grid' && (
                    <div className="category-filter">
                        <button
                            className={`filter-btn ${categoryFilter === null ? 'active' : ''}`}
                            onClick={() => setCategoryFilter(null)}
                        >
                            Tous
                        </button>
                        <button
                            className={`filter-btn ${categoryFilter === 'logo' ? 'active' : ''}`}
                            onClick={() => setCategoryFilter('logo')}
                        >
                            Logos
                        </button>
                        <button
                            className={`filter-btn ${categoryFilter === 'photo' ? 'active' : ''}`}
                            onClick={() => setCategoryFilter('photo')}
                        >
                            Photos
                        </button>
                        <button
                            className={`filter-btn ${categoryFilter === 'icon' ? 'active' : ''}`}
                            onClick={() => setCategoryFilter('icon')}
                        >
                            Icônes
                        </button>
                        <button
                            className={`filter-btn ${categoryFilter === 'banner' ? 'active' : ''}`}
                            onClick={() => setCategoryFilter('banner')}
                        >
                            Bannières
                        </button>
                    </div>
                )}

                {/* Error message */}
                {error && (
                    <div className="media-error-message">
                        {error}
                    </div>
                )}

                {/* Body */}
                <div className="modal-body media-modal-body">
                    {activeTab === 'upload' ? (
                        <MediaUploader
                            onUploadComplete={handleUploadComplete}
                            onError={handleError}
                        />
                    ) : (
                        <MediaGrid
                            media={media}
                            selectedMedia={selectedMedia}
                            onSelectMedia={setSelectedMedia}
                            onDeleteMedia={handleDeleteMedia}
                            onUpdateMedia={handleUpdateMedia}
                            loading={loading}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <div className="footer-info">
                        {selectedMedia && (
                            <span className="selected-info">
                                Sélectionné : <strong>{selectedMedia.filename}</strong>
                            </span>
                        )}
                    </div>
                    <div className="footer-actions">
                        <button className="btn btn-secondary" onClick={onClose}>
                            Annuler
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleSelectAndInsert}
                            disabled={!selectedMedia}
                        >
                            Insérer l'image
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MediaLibraryModal;
